import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Download,
  Search,
  ExternalLink,
} from 'lucide-react'
import MetricCard from './MetricCard'
import styles from './CpoFlow.module.scss'
import { config } from '../../config'

/**
 * Every vin in the CPO csv ends a run in exactly one of these.
 *
 * The distinction that matters: a vin we dropped is not the same as one that
 * never arrived. Being listed in the CPO csv does not mean a car shows up in a
 * provider feed, and "not_in_feed" is not something the feed did wrong.
 */
type Outcome =
  | 'published'
  | 'dropped_unverified'
  | 'dropped_other'
  | 'not_in_feed'
  | 'out_of_scope'

const OUTCOME_LABELS: Record<Outcome, string> = {
  published: 'Published',
  dropped_unverified: 'Dropped, not VIN-verified',
  dropped_other: 'Dropped, other reason',
  not_in_feed: 'Not in the feed',
  out_of_scope: 'Outside the CPO rules',
}

const OUTCOME_HELP: Record<Outcome, string> = {
  published: 'In the CPO csv under a dealer flagged for CPO, VIN-verified, arrived in a provider feed, reached the marketplace. Certified or not — the Certified column says which',
  dropped_unverified: 'Arrived in a provider feed, but FerrariVinVerified was not TRUE, so we dropped it. certification=FALSE alone is not a reason to drop',
  dropped_other: 'Arrived and passed verification, then excluded by something else (duplicate vin, disabled dealer, price rules, unknown make/model)',
  not_in_feed: 'In scope, but never appeared in any provider file — nothing to do with us',
  out_of_scope: 'Listed in the csv under a dealer we do not flag for CPO, or a make Ferrari does not certify. No rule applies to it',
}

const OUTCOME_TYPES: Record<Outcome, 'success' | 'error' | 'warning' | 'neutral'> = {
  published: 'success',
  dropped_unverified: 'error',
  dropped_other: 'warning',
  not_in_feed: 'neutral',
  out_of_scope: 'neutral',
}

/** The feed fields the csv can take over, in the order they are shown. */
const OVERRIDE_KINDS = ['photos', 'model', 'year'] as const
type OverrideKind = typeof OVERRIDE_KINDS[number]

const OVERRIDE_LABELS: Record<OverrideKind, string> = {
  photos: 'Photos',
  model: 'Model',
  year: 'Year',
}

const OVERRIDE_HELP: Record<OverrideKind, string> = {
  photos: "Cars whose photos came from the csv image list instead of the dealer's",
  model: "Cars named from the csv. Written before the make/model rules run, so the remapping sees Ferrari's spelling",
  year: "Cars dated from the csv model_year",
}

interface CpoSummary {
  found: boolean
  date: string
  /** Every row in the csv, whatever the dealer or the make. */
  loaded: number
  /** The rows the rules can act on: a flagged dealer's own Ferraris. */
  in_scope: number
  certified: number
  vin_verified: number
  with_window_sticker: number
  with_carfax_report: number
  overrides: Record<OverrideKind, number>
  outcomes: Record<Outcome, number>
  dropped_by_make: { key: string; count: number }[]
  dropped_by_dealer: { key: string; count: number }[]
  out_of_scope_by_dealer: { key: string; count: number }[]
}

interface CpoVehicle {
  vin: string
  outcome: Outcome
  certified: boolean
  vin_verified: boolean
  has_carfax_report: boolean
  has_window_sticker: boolean
  in_scope?: boolean
  /** Which feed fields this run took from the csv for this vin. */
  overrides?: OverrideKind[]
  make: string
  model: string
  model_year: string
  stock: string
  csv_dealer_id: string
  csv_dealer_name: string
  dealer_name?: string
  correlation_id?: string
}

interface VehiclesResponse {
  total: number
  page: number
  size: number
  maxPage: number
  hits: CpoVehicle[]
}

interface CpoFlowProps {
  datePattern: string
}

const PAGE_SIZE = 25

/** The outcomes each dealer breakdown aggregates, so a drilldown matches it. */
const DROPPED_OUTCOMES: Outcome[] = ['dropped_unverified', 'dropped_other']
const OUT_OF_SCOPE_OUTCOMES: Outcome[] = ['out_of_scope']

/**
 * One request is enough for a dealer bucket: the largest runs to a few hundred
 * vins and the api caps a page at this. A bucket above it is truncated rather
 * than paged, and the expanded panel says so.
 */
const DEALER_FETCH_SIZE = 500

const VEHICLE_CSV_HEADERS = [
  'VIN', 'Outcome', 'In scope', 'Certified', 'VIN verified', 'Year', 'Make', 'Model',
  'Stock', 'CSV dealer', 'Feed dealer', 'Window sticker', 'CARFAX report', 'Overrides',
]

const csvCell = (value: string | undefined) => `"${(value || '').replace(/"/g, '""')}"`

const vehiclesToCsv = (hits: CpoVehicle[]) =>
  VEHICLE_CSV_HEADERS.join(',') + '\n' +
  hits.map((v) => [
    v.vin,
    v.outcome,
    v.in_scope ?? '',
    v.certified,
    v.vin_verified,
    v.model_year,
    csvCell(v.make),
    csvCell(v.model),
    csvCell(v.stock),
    csvCell(v.csv_dealer_name),
    csvCell(v.dealer_name),
    v.has_window_sticker,
    v.has_carfax_report,
    csvCell((v.overrides || []).join(' ')),
  ].join(',')).join('\n') + '\n'

const downloadCsv = (filename: string, csv: string) => {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** A filename fragment that will not surprise anyone on a windows machine. */
const slug = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'dealer'

interface DealerBreakdownProps {
  title: string
  hint?: React.ReactNode
  rows: { key: string; count: number }[]
  /** The outcomes these counts were aggregated over, so a drilldown matches. */
  outcomes: Outcome[]
  datePattern: string
  limit: number
}

/**
 * A dealer bucket list whose rows open to the vins behind them.
 *
 * The counts come from an aggregation and the vins from a search, so the two
 * are fetched separately: a row is only queried when it is opened, and what
 * comes back is kept for as long as the panel is on screen.
 */
const DealerBreakdown: React.FC<DealerBreakdownProps> = ({
  title, hint, rows, outcomes, datePattern, limit,
}) => {
  const [openDealer, setOpenDealer] = useState<string | null>(null)
  const [vinsByDealer, setVinsByDealer] = useState<Record<string, CpoVehicle[]>>({})
  const [loadingDealer, setLoadingDealer] = useState<string | null>(null)
  // The same rows as vinsByDealer, reachable without going through a render.
  // "Export all" walks every bucket in one go, and reading state there would
  // see the snapshot it started from and refetch what it had just stored.
  const cache = useRef<Record<string, CpoVehicle[]>>({})

  // A new date invalidates everything held here.
  useEffect(() => {
    setOpenDealer(null)
    setVinsByDealer({})
    cache.current = {}
  }, [datePattern])

  const fetchDealer = useCallback(async (dealer: string, count: number) => {
    if (cache.current[dealer]) return cache.current[dealer]
    setLoadingDealer(dealer)
    try {
      const params = new URLSearchParams({
        date: datePattern,
        csv_dealer_name: dealer,
        size: String(Math.min(count, DEALER_FETCH_SIZE)),
      })
      outcomes.forEach((o) => params.append('outcome', o))
      const res = await fetch(`${config.MONITOR_API_URL}/insights/cpo-vehicles?${params}`)
      const body: VehiclesResponse = await res.json()
      const hits = body.hits || []
      cache.current[dealer] = hits
      setVinsByDealer((current) => ({ ...current, [dealer]: hits }))
      return hits
    } catch (e) {
      console.error('Failed to fetch CPO vehicles for dealer', dealer, e)
      return []
    } finally {
      setLoadingDealer(null)
    }
  }, [datePattern, outcomes])

  const toggle = (dealer: string, count: number) => {
    if (openDealer === dealer) {
      setOpenDealer(null)
      return
    }
    setOpenDealer(dealer)
    fetchDealer(dealer, count)
  }

  const exportDealer = async (dealer: string, count: number) => {
    const hits = await fetchDealer(dealer, count)
    if (!hits.length) return
    downloadCsv(`cpo-${slug(title)}-${slug(dealer)}-${datePattern}.csv`, vehiclesToCsv(hits))
  }

  const exportAll = async () => {
    const all: CpoVehicle[] = []
    for (const row of rows) {
      all.push(...(await fetchDealer(row.key, row.count)))
    }
    if (!all.length) return
    downloadCsv(`cpo-${slug(title)}-${datePattern}.csv`, vehiclesToCsv(all))
  }

  if (!rows.length) return null

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <button className={styles.exportBtn} onClick={exportAll}>
          <Download size={13} />
          Export all
        </button>
      </div>
      {hint}
      <p className={styles.sectionHint}>Click a dealer to list the vins behind the count.</p>
      <table className={styles.breakdownTable}>
        <tbody>
          {rows.slice(0, limit).map((row) => {
            const open = openDealer === row.key
            const hits = vinsByDealer[row.key]
            return (
              <React.Fragment key={row.key}>
                <tr className={open ? styles.dealerRowOpen : styles.dealerRow}>
                  <td>
                    <button className={styles.dealerToggle} onClick={() => toggle(row.key, row.count)}>
                      {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                      {row.key}
                    </button>
                  </td>
                  <td className={styles.breakdownCount}>
                    {row.count.toLocaleString()}
                    <button
                      className={styles.dealerExport}
                      title="Export these vins as csv"
                      onClick={() => exportDealer(row.key, row.count)}
                    >
                      <Download size={12} />
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={2} className={styles.dealerVins}>
                      {loadingDealer === row.key && !hits && <span>Loading vins...</span>}
                      {hits && !hits.length && <span>No vins came back for this dealer.</span>}
                      {hits && hits.length > 0 && (
                        <>
                          <table className={styles.vinTable}>
                            <thead>
                              <tr>
                                <th>VIN</th>
                                <th>Year</th>
                                <th>Model</th>
                                <th>Stock</th>
                                <th>Certified</th>
                                <th>VIN verified</th>
                              </tr>
                            </thead>
                            <tbody>
                              {hits.map((v) => (
                                <tr key={v.vin}>
                                  <td className={styles.monoValue}>{v.vin}</td>
                                  <td>{v.model_year || '--'}</td>
                                  <td>{v.model || '--'}</td>
                                  <td>{v.stock || '--'}</td>
                                  <td>{v.certified ? 'yes' : '--'}</td>
                                  <td>{v.vin_verified ? 'yes' : '--'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {row.count > hits.length && (
                            <p className={styles.sectionHint}>
                              Showing {hits.length.toLocaleString()} of {row.count.toLocaleString()}.
                              Export carries the same rows.
                            </p>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const CpoFlow: React.FC<CpoFlowProps> = ({ datePattern }) => {
  const [summary, setSummary] = useState<CpoSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)

  const [vehicles, setVehicles] = useState<VehiclesResponse | null>(null)
  const [vehiclesLoading, setVehiclesLoading] = useState(false)

  const [outcome, setOutcome] = useState<Outcome | ''>('')
  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(t)
  }, [searchQuery])

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true)
    try {
      const params = new URLSearchParams({ date: datePattern })
      const res = await fetch(`${config.MONITOR_API_URL}/insights/cpo-summary?${params}`)
      setSummary(await res.json())
    } catch (e) {
      console.error('Failed to fetch CPO summary', e)
    } finally {
      setSummaryLoading(false)
    }
  }, [datePattern])

  const fetchVehicles = useCallback(async () => {
    setVehiclesLoading(true)
    try {
      const params = new URLSearchParams({
        date: datePattern,
        page: String(page),
        size: String(PAGE_SIZE),
      })
      if (outcome) params.set('outcome', outcome)
      if (debouncedSearch) params.set('q', debouncedSearch)
      const res = await fetch(`${config.MONITOR_API_URL}/insights/cpo-vehicles?${params}`)
      setVehicles(await res.json())
    } catch (e) {
      console.error('Failed to fetch CPO vehicles', e)
    } finally {
      setVehiclesLoading(false)
    }
  }, [datePattern, outcome, page, debouncedSearch])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  useEffect(() => {
    fetchVehicles()
  }, [fetchVehicles])

  // Any change to what is being listed sends you back to the first page.
  useEffect(() => {
    setPage(1)
  }, [outcome, debouncedSearch, datePattern])

  const selectOutcome = (next: Outcome) => setOutcome(outcome === next ? '' : next)

  const exportCSV = () => {
    if (!vehicles?.hits.length) return
    downloadCsv(`cpo-flow-${outcome || 'all'}-${datePattern}.csv`, vehiclesToCsv(vehicles.hits))
  }

  if (!summaryLoading && summary && !summary.found) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <AlertTriangle size={32} className={styles.emptyIcon} />
          <p>No CPO flow report for this date.</p>
          <p className={styles.emptySubtitle}>
            The report is written by the feed run. Pick a date on which the pipeline ran.
          </p>
        </div>
      </div>
    )
  }

  const outcomes = summary?.outcomes
  const totalDropped = (outcomes?.dropped_unverified ?? 0) + (outcomes?.dropped_other ?? 0)
  const outOfScope = outcomes?.out_of_scope ?? 0
  const published = outcomes?.published ?? 0
  // Reports written before scoping existed report no in_scope count. Falling
  // back to the file total keeps an old date's ratios at 100% rather than
  // dividing by zero.
  const inScope = summary?.in_scope ?? summary?.loaded ?? 0

  return (
    <div className={styles.container}>
      {/* What the csv held for the dealers the rules actually cover. Every
          count but "Vins loaded" is measured inside that scope: the file also
          lists other dealers and other makes, and mixing them in made each of
          these read far worse than the run behaved. */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>In the CPO file</h3>
          <button className={styles.refreshBtn} onClick={() => { fetchSummary(); fetchVehicles() }}>
            <RefreshCw size={13} className={summaryLoading ? styles.spinning : ''} />
            Refresh
          </button>
        </div>
        <p className={styles.sectionHint}>
          <strong>In scope</strong> is the part of the file the rules can reach: rows under a
          dealer flagged <code>is_cpo_dealer</code> whose <code>cpo_dealer_id</code> matches, for a
          make Ferrari certifies. Everything below is counted inside it.
        </p>
        <div className={styles.metricsGrid}>
          <MetricCard title="Vins in the file" value={summary?.loaded ?? 0} type="total" loading={summaryLoading} />
          <MetricCard title="In scope" value={inScope} total={summary?.loaded} type="info" loading={summaryLoading} />
          <MetricCard title="Certified" value={summary?.certified ?? 0} total={inScope} type="info" loading={summaryLoading} />
          <MetricCard title="VIN verified" value={summary?.vin_verified ?? 0} total={inScope} type="info" loading={summaryLoading} />
          <MetricCard title="With window sticker" value={summary?.with_window_sticker ?? 0} total={inScope} type="neutral" loading={summaryLoading} />
          <MetricCard title="With CARFAX report" value={summary?.with_carfax_report ?? 0} total={inScope} type="neutral" loading={summaryLoading} />
        </div>
      </div>

      {/* What the run took from the csv rather than the dealer. The gap between
          a published car and an override is the fallback: a blank column leaves
          the dealer's own value alone. Price is never one of these - the car
          keeps whatever the dealer sent. */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Overrides applied</h3>
        <p className={styles.sectionHint}>
          Out of {published.toLocaleString()} published, how many took each field from the csv.
          A shortfall is a fallback, not a failure: the csv carried nothing usable, so the value
          from the dealer stood.
        </p>
        <div className={styles.metricsGrid}>
          {OVERRIDE_KINDS.map((kind) => (
            <div key={kind} title={OVERRIDE_HELP[kind]}>
              <MetricCard
                title={OVERRIDE_LABELS[kind]}
                value={summary?.overrides?.[kind] ?? 0}
                total={published}
                type="info"
                loading={summaryLoading}
              />
            </div>
          ))}
        </div>
      </div>

      {/* What the feed did with them */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>What happened to them</h3>
        <p className={styles.sectionHint}>
          Click an outcome to list the vehicles behind it. Every row in the file is in exactly one,
          so these add up to {(summary?.loaded ?? 0).toLocaleString()}. Being in the CPO file does
          not mean a car turns up in a provider feed, so <strong>not in the feed</strong> is not a
          drop, and <strong>outside the CPO rules</strong> is not one either.
        </p>
        <div className={styles.outcomeGrid}>
          {(Object.keys(OUTCOME_LABELS) as Outcome[]).map((key) => (
            <button
              key={key}
              className={`${styles.outcomeTile} ${outcome === key ? styles.outcomeTileActive : ''}`}
              onClick={() => selectOutcome(key)}
              title={OUTCOME_HELP[key]}>
              <MetricCard
                title={OUTCOME_LABELS[key]}
                value={outcomes?.[key] ?? 0}
                total={summary?.loaded}
                type={OUTCOME_TYPES[key]}
                invertDelta={key !== 'published'}
                loading={summaryLoading}
              />
            </button>
          ))}
        </div>
      </div>

      {/* The make split is the interesting one: the csv covers every make these
          dealers carry, not only Ferrari. */}
      {totalDropped > 0 && (
        <>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Dropped by make</h3>
            <table className={styles.breakdownTable}>
              <tbody>
                {summary?.dropped_by_make.slice(0, 10).map((row) => (
                  <tr key={row.key}>
                    <td>{row.key}</td>
                    <td className={styles.breakdownCount}>{row.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Full width rather than beside the make split: an opened row lists
              its vins, and that does not fit a half column. */}
          <DealerBreakdown
            title="Dropped by dealer"
            rows={summary?.dropped_by_dealer ?? []}
            outcomes={DROPPED_OUTCOMES}
            datePattern={datePattern}
            limit={10}
          />
        </>
      )}

      {/* A Ferrari dealer sitting here is one nobody has flagged yet: either
          is_cpo_dealer is off or cpo_dealer_id is unset, and their cars are
          going through as ordinary listings. */}
      {outOfScope > 0 && !!summary?.out_of_scope_by_dealer?.length && (
        <DealerBreakdown
          title="Outside the rules, by csv dealer"
          hint={
            <p className={styles.sectionHint}>
              These rows are in the file but no rule touches them. A dealer you expected to see
              published here is one to check in the django admin: either <code>is_cpo_dealer</code> is
              off, or <code>cpo_dealer_id</code> does not match the csv.
            </p>
          }
          rows={summary.out_of_scope_by_dealer}
          outcomes={OUT_OF_SCOPE_OUTCOMES}
          datePattern={datePattern}
          limit={15}
        />
      )}

      {/* Drilldown */}
      <div className={styles.section}>
        <div className={styles.controls}>
          <h3 className={styles.sectionTitle}>
            {outcome ? OUTCOME_LABELS[outcome] : 'All vehicles'}
            <span className={styles.summaryCount}>{(vehicles?.total ?? 0).toLocaleString()}</span>
          </h3>
          <div className={styles.controlSpacer} />
          <div className={styles.searchWrap}>
            <Search size={13} />
            <input
              className={styles.searchInput}
              placeholder="VIN, stock, make, model, dealer"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {outcome && (
            <button className={styles.clearBtn} onClick={() => setOutcome('')}>
              Clear filter
            </button>
          )}
          <button className={styles.exportBtn} onClick={exportCSV} disabled={!vehicles?.hits.length}>
            <Download size={13} />
            Export CSV
          </button>
        </div>

        {outcome && <p className={styles.sectionHint}>{OUTCOME_HELP[outcome]}</p>}

        <div className={styles.tableWrap}>
          <table className={styles.vehicleTable}>
            <thead>
              <tr>
                <th>VIN</th>
                <th>Outcome</th>
                <th>Year</th>
                <th>Make</th>
                <th>Model</th>
                <th>Stock</th>
                <th>Dealer (csv)</th>
                <th>Certified</th>
                <th>Verified</th>
                <th>Overrides</th>
                <th>Docs</th>
              </tr>
            </thead>
            <tbody>
              {vehiclesLoading && (
                <tr><td colSpan={11} className={styles.loading}>Loading vehicles...</td></tr>
              )}
              {!vehiclesLoading && !vehicles?.hits.length && (
                <tr><td colSpan={11} className={styles.loading}>No vehicles match.</td></tr>
              )}
              {!vehiclesLoading && vehicles?.hits.map((v) => (
                <tr key={v.vin}>
                  <td className={styles.monoValue}>{v.vin}</td>
                  <td>
                    <span className={`${styles.badge} ${styles[`badge_${v.outcome}`]}`}>
                      {OUTCOME_LABELS[v.outcome]}
                    </span>
                  </td>
                  <td>{v.model_year}</td>
                  <td>{v.make}</td>
                  <td>{v.model}</td>
                  <td className={styles.monoValue}>{v.stock}</td>
                  <td>{v.csv_dealer_name}</td>
                  <td>{v.certified ? 'yes' : '—'}</td>
                  <td>{v.vin_verified ? 'yes' : '—'}</td>
                  <td>
                    <div className={styles.overrideCell}>
                      {v.overrides?.length
                        ? v.overrides.map((kind) => (
                            <span key={kind} className={styles.overrideTag} title={OVERRIDE_HELP[kind]}>
                              {kind}
                            </span>
                          ))
                        : '—'}
                    </div>
                  </td>
                  <td className={styles.docsCell}>
                    {v.has_window_sticker && <span title="Window sticker">WS</span>}
                    {v.has_carfax_report && <span title="CARFAX report">CFX</span>}
                    {v.correlation_id && (
                      <span title={v.correlation_id}><ExternalLink size={11} /></span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!!vehicles?.total && vehicles.total > PAGE_SIZE && (
          <div className={styles.pagination}>
            <span className={styles.pageInfo}>
              Page {vehicles.page} of {vehicles.maxPage}
            </span>
            <div className={styles.pageControls}>
              <button
                className={styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft size={14} />
              </button>
              <button
                className={styles.pageBtn}
                disabled={page >= (vehicles.maxPage || 1)}
                onClick={() => setPage((p) => p + 1)}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CpoFlow
